import { customElement } from 'lit/decorators.js';
import { FabricationJobState, type FabricationJobSummaryView } from '../../../api/fabrication-job-api';
import { getApiFactory } from '../../../api/api-injector';
import {
  FabricationJobSummaryTable,
  type FabricationJobSummaryTableOptions
} from '../../../fabrication-jobs/views/fabrication-job-summary-table';
import { html, TemplateResult } from 'lit';
import { LangFabricationJobPicker } from '../../../../localization/scripts/fabrication-jobs/views/fabrication-job-picker-lang';
import { LangFabricationJobSummaryTable } from '../../../../localization/scripts/fabrication-jobs/views/fabrication-job-summary-table-lang';
import {
  ModalPickerDialog,
  type ModalPickerDialogOptions
} from '../../../../webmodule-common/other/ui/modal-picker-base';
import { newGuid } from '../../../../webmodule-common/other/api/guid';
import type { NullPromise } from '../../../../webmodule-common/other/ui/events';
import type {
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../../webmodule-common/components/src/webmodule-components';

interface FabricationJobPickerBrowserOptions extends FabricationJobSummaryTableOptions {
  onJobSelect?: (job: FabricationJobSummaryView) => void;
}

@customElement('wm-fabricationjobpickerbrowser')
class FabricationJobPickerBrowser extends FabricationJobSummaryTable {
  onJobSelect?: (job: FabricationJobSummaryView) => void;

  constructor(options: FabricationJobPickerBrowserOptions) {
    super(options);
    this.onJobSelect = options.onJobSelect;
    this.clickrows = true;
  }

  override isDataLoadDelayed(): boolean {
    return false;
  }

  // A modal picker sizes to its content, not to a bounded flex region, so the fill-height
  // behaviour the list page wants would leave the table with no height to fill.
  override fillHeight(): boolean {
    return false;
  }

  // The jobs list's columns, narrowed to the three a picker needs and with the links swapped
  // for plain text - in the picker the row itself is the action. Derived from
  // super.getColumns() rather than re-declared so the sizing has one home: the picker and the
  // list sit side by side, and hand-copied widths had already drifted apart once.
  override getColumns(): WebModuleTableColumnDef[] {
    // Keys double as the whitelist below: these three columns, and no others.
    const plainTextColumns: Record<string, (job: FabricationJobSummaryView) => TemplateResult> = {
      jobNumber: job => html`${job.jobNumber}`,
      title: job => html`${job.title || LangFabricationJobSummaryTable.untitled()}`,
      itemCount: job => html`${job.itemCount}`
    };
    return (
      super
        .getColumns()
        // Object.hasOwn, not `in`: `in` walks the prototype chain, so a column named
        // `toString`/`constructor`/`valueOf` would pass the whitelist and then be "rendered"
        // by calling Object.prototype.toString, returning a string where a TemplateResult
        // is expected. Own keys only is what "the keys are the whitelist" actually means.
        .filter(column => Object.hasOwn(plainTextColumns, column.fieldName))
        .map(column => ({
          ...column,
          displayValue: (_table: WebModuleTable, item: unknown) =>
            plainTextColumns[column.fieldName](item as FabricationJobSummaryView)
        }))
    );
  }

  override async rowClicked(e: CustomEvent<{ table: WebModuleTable; item: FabricationJobSummaryView }>): Promise<void> {
    e.stopImmediatePropagation();
    e.preventDefault();

    this.eventHandler(async () => {
      this.onJobSelect?.(e.detail.item);
    });
  }
}

interface FabricationJobPickerOptions extends ModalPickerDialogOptions<FabricationJobSummaryView> {
  branchId: string;
}

class FabricationJobBrowserPicker extends ModalPickerDialog<FabricationJobSummaryView> {
  private browser: FabricationJobPickerBrowser;
  private branchId: string;

  constructor(options: FabricationJobPickerOptions) {
    super(options);
    this.branchId = options.branchId;

    this.browser = new FabricationJobPickerBrowser({
      title: () => options.title ?? '',
      branchId: async () => options.branchId,
      states: [FabricationJobState.Draft],
      pageFragment: '',
      onJobSelect: job => this.select(job)
    });
  }

  protected get modalSize(): string {
    return 'modal-lg';
  }

  bodyTemplate() {
    return html`${this.browser}`;
  }

  footerTemplate() {
    return html`
      <webmodule-button size="small" variant="default" @click=${() => this.createNewJob()}>
        ${LangFabricationJobPicker.createNewJob()}
      </webmodule-button>
      <webmodule-button size="small" variant="default" @click=${() => this.cancel()}>
        ${LangFabricationJobPicker.cancel()}
      </webmodule-button>
    `;
  }

  private async createNewJob(): Promise<void> {
    const api = getApiFactory().fabricationJob();
    const id = newGuid();
    const result = await api.createFabricationJob({
      id,
      branchId: this.branchId,
      title: '',
      description: ''
    });
    if (result) {
      const job = result.fabricationJob;
      await this.select({
        id: job.id,
        recordVersion: job.recordVersion,
        jobNumber: job.jobNumber,
        title: job.title,
        state: job.state,
        itemCount: 0,
        distinctQuoteItemIds: [],
        dateCreated: job.dateCreated,
        lastModifiedDate: job.lastModifiedDate,
        assignedToUserId: job.assignedToUserId
      });
    }
  }
}

export async function fabricationJobPicker(branchId: string): NullPromise<FabricationJobSummaryView> {
  return new Promise<FabricationJobSummaryView | null>(resolve => {
    const picker = new FabricationJobBrowserPicker({
      onSelect: (selected: FabricationJobSummaryView | null) => {
        resolve(selected);
      },
      onCancel: () => resolve(null),
      title: LangFabricationJobPicker.selectFabricationJob(),
      branchId
    });
    picker.showModal().then(() => resolve(null));
  });
}
